/**
 * ユーザー向け文章の単一辞書（UI / API / メール共通）。
 *
 * 設計意図 (issue #43):
 *   - 画面・API toast・通知メールで同じ語彙を使う
 *   - 技術用語（Stream, テナント, 失効, 凍結 等）を排除する
 *   - 判断基準は docs/ui-copy-guide.md
 */

export const USER_MESSAGES = {
  /** 共有URLが使えない（失効・期限切れ・無効をユーザー視点で同一化） */
  shareLinkUnavailable: 'この共有URLは現在ご利用いただけません。発行元に再発行をご依頼ください。',

  /** 共有URLが見つからない */
  shareLinkNotFound: 'URL を再度ご確認ください。改めて発行元から正しい URL を受け取ってください。',

  /** ログイン時: すでにお休み状態 */
  accountFrozen: 'アカウントがお休み状態です。有料プランのお申し込みで再開してください。',

  /** ログイン時: 60日未利用でお休み状態に移行 */
  accountAutoFrozen:
    '60日以上ログインがなかったため、アカウントがお休み状態になりました。有料プランのお申し込みで再開してください。',

  /** お試し中の共有URL権限制約 */
  ephemeralShareDownloadOnly:
    'お試し中は「ダウンロードもできる」共有だけ選べます。権限を変更してください。',

  /** お試し中のメール必須制約 */
  ephemeralEmailRequiredFixed:
    'お試し中はメールアドレス入力を必須にできません。設定はそのままでご利用ください。',

  /** お試し中の共有URL期限制約 */
  ephemeralShareExpiryExceeded:
    '共有URLの期限が、お試しの終了日を超えています。終了日より前の日付を選んでください。',

  /** お試し中の共有URL本数制約 */
  ephemeralShareLinkCountLimit:
    'お試し中は共有URLを1本までしか作れません。既存のURLを使えなくしてから作り直してください。',

  /** 動画の内部取り込み未完了 */
  videoPreparing: '動画の準備がまだ終わっていません。しばらく待ってからもう一度お試しください。',

  /** 動画が再生可能状態でない */
  videoNotPlayable: '動画はまだ再生できません。準備が終わるまでお待ちください。',

  /** アップロード未完了 */
  uploadNotComplete: 'まだアップロードが完了していません。完了してからもう一度お試しください。',

  /** プレビュー非対応のファイル種別 */
  previewNotSupported: 'この種別はプレビューできません。ダウンロードしてご確認ください。',

  /** 共有URL着地でメール必須 */
  emailRequiredForShare: 'メールアドレスを入力してください。',

  /** ストレージ上限 */
  storageLimitExceeded:
    'ストレージ容量の上限に達しました。不要なファイルをゴミ箱に移すか、有料プランをご検討ください。',

  /** 共有URLの対象ファイル不一致 */
  shareLinkFileNotInProject:
    '選んだファイルがプロジェクトに含まれていません。ファイルを選び直してください。',

  /** 通知先メールアドレス: いまの通知先と同じアドレスが入力された */
  notificationEmailUnchanged:
    'いまの通知先と同じメールアドレスです。別のメールアドレスを入力してください。',

  /** 通知先メールアドレス: 確認メールを続けて送ろうとした */
  notificationEmailResendTooSoon:
    '確認メールを送ったばかりです。1分ほど待ってから、もう一度お試しください。',

  /** 通知先メールアドレス: 24時間に送れる確認メールの数を超えた */
  notificationEmailDailyLimit:
    '確認メールを送れる回数の上限に達しました。24時間ほど待ってから、もう一度お試しください。',

  /** 通知先メールアドレス: 確認メールを送れなかった */
  notificationEmailSendFailed:
    '確認メールを送れませんでした。メールアドレスをお確かめのうえ、少し時間を置いてからもう一度お試しください。',

  /** 通知先メールアドレス: 確認リンクが無効（使用済み・別の手続きで上書き済みを含む） */
  notificationEmailLinkInvalid:
    'このリンクは使えません。設定画面から、もう一度通知先の変更を手続きしてください。',

  /** 通知先メールアドレス: 確認リンクの期限切れ */
  notificationEmailLinkExpired:
    'リンクの有効期限（24時間）が切れています。設定画面から、もう一度通知先の変更を手続きしてください。',
} as const

export type UserMessageKey = keyof typeof USER_MESSAGES

/** 全ユーザーメッセージの値（テスト用） */
export const ALL_USER_MESSAGE_VALUES = Object.values(USER_MESSAGES)
